/* eslint-disable max-lines*/

import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  Columns3,
  File,
  FileText,
  LayoutTemplate,
  MoreVertical,
  PencilIcon,
  Play,
  Plus,
  Upload,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

type DemoColumn = {
  id: string
  name: string
  type: "text" | "number" | "boolean" | "date"
  description: string
}

type DemoDocument = {
  id: string
  name: string
  extractedValues: Record<string, string | number | boolean | null>
}

const SAMPLE_DOCUMENTS = [
  { id: "1", name: "faktura_2024_001.pdf" },
  { id: "2", name: "faktura_2024_002.pdf" },
  { id: "3", name: "faktura_2024_003.pdf" },
]

const SAMPLE_EXTRACTED_DATA: Record<string, Record<string, string | number | boolean | null>> = {
  "1": {
    invoice_number: "INV-2024-001",
    amount: 15750,
    due_date: "2024-03-15",
    is_paid: true,
    supplier: "Acme AB",
  },
  "2": {
    invoice_number: "INV-2024-002",
    amount: 8900,
    due_date: "2024-03-22",
    is_paid: false,
    supplier: "TechSupply Nordic",
  },
  "3": {
    invoice_number: "INV-2024-003",
    amount: 23400,
    due_date: "2024-04-01",
    is_paid: false,
    supplier: "DataServices Sverige AB",
  },
}

function getDefaultColumns(t: (key: string) => string): DemoColumn[] {
  return [
    {
      id: "invoice_number",
      name: t("default_columns.invoice_number.name"),
      type: "text",
      description: t("default_columns.invoice_number.description"),
    },
    {
      id: "amount",
      name: t("default_columns.amount.name"),
      type: "number",
      description: t("default_columns.amount.description"),
    },
    {
      id: "due_date",
      name: t("default_columns.due_date.name"),
      type: "date",
      description: t("default_columns.due_date.description"),
    },
    {
      id: "is_paid",
      name: t("default_columns.is_paid.name"),
      type: "boolean",
      description: t("default_columns.is_paid.description"),
    },
  ]
}

export function DemoAIParser() {
  const { t } = useTranslation("", { keyPrefix: "training.demo.ai_parser" })

  const [documents, setDocuments] = useState<DemoDocument[]>([])
  const [columns, setColumns] = useState<DemoColumn[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [hasRun, setHasRun] = useState(false)
  const [isAddColumnOpen, setIsAddColumnOpen] = useState(false)
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false)

  const [newColumn, setNewColumn] = useState<Partial<DemoColumn>>({
    name: "",
    type: "text",
    description: "",
  })

  const handleAddSampleDocuments = useCallback(() => {
    setDocuments(SAMPLE_DOCUMENTS.map((doc) => ({ ...doc, extractedValues: {} })))
    setIsAddDocumentOpen(false)
    toast.success(t("documents_added"))
    setHasRun(false)
  }, [t])

  const handleAddDefaultColumns = useCallback(() => {
    setColumns(getDefaultColumns(t))
    setIsAddColumnOpen(false)
    toast.success(t("columns_added"))
    setHasRun(false)
  }, [t])

  const handleAddColumn = useCallback(() => {
    if (!newColumn.name || !newColumn.description) {
      toast.error(t("column_missing_fields"))
      return
    }

    const columnToAdd: DemoColumn = {
      id: newColumn.name.toLowerCase().replace(/\s+/g, "_"),
      name: newColumn.name,
      type: newColumn.type || "text",
      description: newColumn.description,
    }

    setColumns((prev) => [...prev, columnToAdd])
    setNewColumn({ name: "", type: "text", description: "" })
    setIsAddColumnOpen(false)
    toast.success(t("column_added"))
    setHasRun(false)
  }, [newColumn, t])

  const handleRemoveDocument = useCallback((documentId: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== documentId))
    setHasRun(false)
  }, [])

  const handleRemoveColumn = useCallback((columnId: string) => {
    setColumns((prev) => prev.filter((col) => col.id !== columnId))
    setHasRun(false)
  }, [])

  const handleRun = useCallback(async () => {
    if (documents.length === 0 || columns.length === 0) {
      toast.error(t("run_requirements"))
      return
    }

    setIsRunning(true)

    for (let docIndex = 0; docIndex < documents.length; docIndex++) {
      await new Promise((resolve) => setTimeout(resolve, 600))

      setDocuments((prev) =>
        prev.map((doc, index) => {
          if (index !== docIndex) return doc

          const sampleData = SAMPLE_EXTRACTED_DATA[doc.id] ?? {}
          const extractedValues: Record<string, string | number | boolean | null> = {}

          columns.forEach((col) => {
            if (col.id in sampleData) {
              extractedValues[col.id] = sampleData[col.id]
            } else {
              extractedValues[col.id] = null
            }
          })

          return { ...doc, extractedValues }
        })
      )
    }

    setIsRunning(false)
    setHasRun(true)
    toast.success(t("run_complete"))
  }, [documents, columns, t])

  const handleExport = useCallback(() => {
    toast.success(t("export_success"))
  }, [t])

  const handleReset = useCallback(() => {
    setDocuments([])
    setColumns([])
    setHasRun(false)
  }, [])

  const canRun = documents.length > 0 && columns.length > 0 && !isRunning

  return (
    <div className="border-border bg-background overflow-hidden rounded-lg border shadow-sm">
      {/* Header - matches AIParserHeader styling */}
      <div className="border-border bg-background sticky top-0 flex flex-col gap-2 border-b p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="text-foreground flex items-center gap-2">
          <h2 className="!mt-0 text-base font-semibold sm:text-lg">{t("session_title")}</h2>
          <Button disabled size="icon" variant="ghost">
            <PencilIcon className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <Button
            className="h-8 px-2 sm:h-9 sm:px-3"
            disabled={isRunning}
            size="sm"
            variant="outline"
          >
            <LayoutTemplate className="h-4 w-4" />
            <span className="hidden sm:inline">{t("templates")}</span>
          </Button>

          <Dialog open={isAddDocumentOpen} onOpenChange={setIsAddDocumentOpen}>
            <DialogTrigger asChild>
              <Button
                className="h-8 px-2 sm:h-9 sm:px-3"
                disabled={isRunning}
                size="sm"
                variant="outline"
              >
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">{t("add_documents")}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("add_documents_title")}</DialogTitle>
                <DialogDescription>{t("add_documents_description")}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <Button
                  className="h-auto w-full py-4 text-base font-medium"
                  onClick={handleAddSampleDocuments}
                >
                  {t("add_sample_invoices")}
                </Button>
                <div className="text-muted-foreground text-center text-sm">{t("or")}</div>
                <div className="border-muted-foreground/30 bg-muted/30 cursor-not-allowed rounded-lg border-2 border-dashed p-8 text-center opacity-50">
                  <FileText className="text-muted-foreground mx-auto mb-2 h-10 w-10" />
                  <p className="text-muted-foreground text-sm">{t("drag_drop_hint")}</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isAddColumnOpen} onOpenChange={setIsAddColumnOpen}>
            <DialogTrigger asChild>
              <Button
                className="h-8 px-2 sm:h-9 sm:px-3"
                disabled={isRunning}
                size="sm"
                variant="outline"
              >
                <Columns3 className="h-4 w-4" />
                <span className="hidden sm:inline">{t("add_columns")}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("add_columns_title")}</DialogTitle>
                <DialogDescription>{t("add_columns_description")}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <Button
                  className="h-auto w-full py-4 text-base font-medium"
                  onClick={handleAddDefaultColumns}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t("add_invoice_columns")}
                </Button>
                <div className="flex items-center gap-2">
                  <div className="bg-border h-px flex-1" />
                  <span className="text-muted-foreground text-sm">{t("or_create_custom")}</span>
                  <div className="bg-border h-px flex-1" />
                </div>
                <CustomColumnForm newColumn={newColumn} setNewColumn={setNewColumn} />
              </div>
              <DialogFooter>
                <Button disabled onClick={handleAddColumn}>
                  {t("add_column_button")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            className="h-8 px-2 sm:h-9 sm:px-3"
            disabled={!hasRun || isRunning}
            size="sm"
            variant="outline"
            onClick={handleExport}
          >
            <Upload className="h-4 w-4" />
            <span className="hidden sm:inline">{t("export")}</span>
          </Button>

          <Button
            className="h-8 px-2 sm:h-9 sm:px-3"
            disabled={!canRun}
            size="sm"
            onClick={handleRun}
          >
            {isRunning ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            <span className="xs:inline hidden">{isRunning ? t("running") : t("run")}</span>
          </Button>
        </div>
      </div>

      {/* Table - matches real AI Parser table */}
      <div className="bg-background relative overflow-x-auto">
        <Table className="table-fixed">
          <TableHeader className="bg-background sticky top-0 z-10 [&_tr]:border-b">
            <TableRow>
              <TableHead className="w-[140px] min-w-[140px] border-r p-0 sm:w-[200px] sm:min-w-[200px]">
                <ColumnHeader>{t("document")}</ColumnHeader>
              </TableHead>
              {columns.map((col, colIndex) => (
                <TableHead
                  key={col.id}
                  className={cn(
                    "w-[120px] min-w-[120px] p-0 sm:w-[200px] sm:min-w-[200px]",
                    colIndex < columns.length - 1 && "border-r"
                  )}
                >
                  <ColumnHeader className="justify-between">
                    <span className="truncate font-bold capitalize">{col.name}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="size-6 shrink-0 p-0"
                          disabled={isRunning}
                          size="sm"
                          variant="ghost"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => handleRemoveColumn(col.id)}
                        >
                          {t("delete_column")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ColumnHeader>
                </TableHead>
              ))}
              {columns.length === 0 && (
                <TableHead className="w-[200px] min-w-[200px] p-0 sm:w-[300px] sm:min-w-[300px]">
                  <ColumnHeader className="text-muted-foreground">
                    {t("no_columns_hint")}
                  </ColumnHeader>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell
                  className="text-muted-foreground h-32 text-center"
                  colSpan={columns.length + 1 || 2}
                >
                  {t("no_documents_hint")}
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc, docIndex) => (
                <TableRow key={doc.id}>
                  <TableCell className="border-r p-1.5 sm:p-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                      <span className="text-muted-foreground shrink-0 text-xs sm:text-sm">
                        {docIndex + 1}
                      </span>
                      <ColumnCell className="flex max-w-none min-w-0 flex-1 items-center justify-between gap-1">
                        <File className="size-3.5 shrink-0 sm:size-4" />
                        <span
                          className="min-w-0 flex-1 shrink overflow-hidden text-xs text-ellipsis whitespace-nowrap sm:text-sm"
                          title={doc.name}
                        >
                          {doc.name}
                        </span>
                        <Button
                          className="text-muted-foreground ml-1 !size-6 shrink-0 rounded-full !p-0 sm:ml-2 sm:!size-7"
                          disabled={isRunning}
                          variant="ghost"
                          onClick={() => handleRemoveDocument(doc.id)}
                        >
                          <X className="size-3.5 sm:size-4" />
                        </Button>
                      </ColumnCell>
                    </div>
                  </TableCell>
                  {columns.map((col, colIndex) => (
                    <TableCell
                      key={col.id}
                      className={cn("p-1.5 sm:p-2", colIndex < columns.length - 1 && "border-r")}
                    >
                      <CellValue
                        columnType={col.type}
                        isRunning={isRunning && !doc.extractedValues[col.id]}
                        value={doc.extractedValues[col.id]}
                      />
                    </TableCell>
                  ))}
                  {columns.length === 0 && <TableCell />}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {/* Mobile scroll hint - visible when there are columns */}
        {columns.length > 0 && (
          <div className="from-background pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l to-transparent sm:hidden" />
        )}
      </div>

      {/* Footer with reset */}
      {(documents.length > 0 || columns.length > 0) && (
        <div className="border-border border-t p-2 text-right">
          <Button disabled={isRunning} size="sm" variant="ghost" onClick={handleReset}>
            {t("reset")}
          </Button>
        </div>
      )}
    </div>
  )
}

function ColumnHeader({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex h-10 items-center gap-1.5 px-2 font-bold sm:h-12 sm:gap-2 sm:px-3",
        className
      )}
    >
      {children}
    </div>
  )
}

function ColumnCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "bg-muted/50 flex w-full items-center justify-between gap-1 rounded-[4px] px-2 py-0.5 text-xs sm:gap-2 sm:px-3 sm:py-1 sm:text-sm",
        className
      )}
    >
      {children}
    </div>
  )
}

function CustomColumnForm({
  newColumn,
  setNewColumn,
}: {
  newColumn: Partial<DemoColumn>
  setNewColumn: React.Dispatch<React.SetStateAction<Partial<DemoColumn>>>
}) {
  const { t } = useTranslation("", { keyPrefix: "training.demo.ai_parser" })

  return (
    <div className="cursor-not-allowed space-y-3 opacity-50">
      <div>
        <Label className="text-muted-foreground" htmlFor="col-name">
          {t("column_name")}
        </Label>
        <Input
          disabled
          id="col-name"
          placeholder={t("column_name_placeholder")}
          value={newColumn.name}
          onChange={(event) => setNewColumn((prev) => ({ ...prev, name: event.target.value }))}
        />
      </div>
      <div>
        <Label className="text-muted-foreground" htmlFor="col-type">
          {t("column_type")}
        </Label>
        <Select
          disabled
          value={newColumn.type}
          onValueChange={(value: "text" | "number" | "boolean" | "date") =>
            setNewColumn((prev) => ({ ...prev, type: value }))
          }
        >
          <SelectTrigger id="col-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">{t("type_text")}</SelectItem>
            <SelectItem value="number">{t("type_number")}</SelectItem>
            <SelectItem value="boolean">{t("type_boolean")}</SelectItem>
            <SelectItem value="date">{t("type_date")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-muted-foreground" htmlFor="col-desc">
          {t("column_description")}
        </Label>
        <Textarea
          disabled
          id="col-desc"
          placeholder={t("column_description_placeholder")}
          rows={3}
          value={newColumn.description}
          onChange={(event) =>
            setNewColumn((prev) => ({ ...prev, description: event.target.value }))
          }
        />
      </div>
    </div>
  )
}

function CellValue({
  value,
  columnType,
  isRunning,
}: {
  value: string | number | boolean | null | undefined
  columnType: string
  isRunning: boolean
}) {
  const { t } = useTranslation("", { keyPrefix: "training.demo.ai_parser" })

  if (isRunning) {
    return (
      <ColumnCell>
        <span className="text-muted-foreground">{t("extracting")}</span>
        <Spinner className="size-3.5 sm:size-4" />
      </ColumnCell>
    )
  }

  if (value === undefined) {
    return <div className="h-6 sm:h-8" />
  }

  if (value === null) {
    return (
      <ColumnCell>
        <span className="text-muted-foreground">{t("not_available")}</span>
      </ColumnCell>
    )
  }

  if (typeof value === "boolean") {
    return (
      <ColumnCell
        className={cn({
          "bg-success/15 border-success text-foreground border": value,
          "bg-destructive/15 border-destructive text-foreground border": !value,
        })}
      >
        <span className="font-medium">{value ? t("yes") : t("no")}</span>
      </ColumnCell>
    )
  }

  if (columnType === "number" && typeof value === "number") {
    return (
      <ColumnCell>
        <span>{value.toLocaleString("sv-SE")} kr</span>
      </ColumnCell>
    )
  }

  return (
    <ColumnCell>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{String(value)}</span>
    </ColumnCell>
  )
}
